import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Sequence,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {brand, brandFont} from '../brand';
import {BgGradient} from '../components/BgGradient';

export type KineticTypeProps = {
  lines: {text: string; accent?: 'primary' | 'action' | 'success' | 'white'}[];
  finalLine: string;
};

const LINE_FRAMES = 36;
const FINAL_HOLD = 90;

export const KineticType: React.FC<KineticTypeProps> = ({lines, finalLine}) => (
  <AbsoluteFill>
    <BgGradient />
    <Audio src={staticFile('music/hook-reel-2.mp3')} volume={0.4} />

    {lines.map((line, i) => (
      <Sequence
        key={i}
        from={i * LINE_FRAMES}
        durationInFrames={LINE_FRAMES + 8}
      >
        <Line text={line.text} accent={line.accent ?? 'white'} />
      </Sequence>
    ))}

    <Sequence
      from={lines.length * LINE_FRAMES}
      durationInFrames={FINAL_HOLD}
    >
      <FinalLine text={finalLine} />
    </Sequence>
  </AbsoluteFill>
);

const accentColor = (accent: 'primary' | 'action' | 'success' | 'white') => {
  if (accent === 'primary') return brand.primaryLight;
  if (accent === 'action') return brand.action;
  if (accent === 'success') return brand.success;
  return brand.textPrimary;
};

const Line: React.FC<{text: string; accent: 'primary' | 'action' | 'success' | 'white'}> = ({
  text,
  accent,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const slamSpring = spring({
    frame,
    fps,
    from: 0,
    to: 1,
    config: {damping: 12, stiffness: 130},
  });

  const scale = interpolate(slamSpring, [0, 1], [1.6, 1]);
  const opacity = interpolate(frame, [0, 6, LINE_FRAMES - 6, LINE_FRAMES], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 60px',
      }}
    >
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 168,
          fontWeight: 900,
          color: accentColor(accent),
          letterSpacing: -6,
          lineHeight: 0.92,
          textAlign: 'center',
          whiteSpace: 'pre-line',
          transform: `scale(${scale})`,
          opacity,
          textShadow: '0 12px 40px rgba(0,0,0,0.7)',
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  );
};

const FinalLine: React.FC<{text: string}> = ({text}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const enterSpring = spring({
    frame,
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 100},
  });

  const scale = interpolate(enterSpring, [0, 1], [0.6, 1]);
  const opacity = interpolate(frame, [0, 10], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 60px',
      }}
    >
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 132,
          fontWeight: 900,
          letterSpacing: -4,
          lineHeight: 0.96,
          textAlign: 'center',
          whiteSpace: 'pre-line',
          transform: `scale(${scale})`,
          opacity,
          background: `linear-gradient(135deg, ${brand.primaryLight} 0%, ${brand.action} 100%)`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textShadow: '0 12px 40px rgba(0,0,0,0.55)',
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  );
};
